import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import { PaginationDirection } from '../../../__generated__/globalTypes';

import BaseObligationsTable from './BaseObligationsTable';
import { ObligationsAmountFormat, ObligationsDateFormat } from './BaseObligationsTable';

import type {
  GetPaidObligationsForUserQuery,
  GetPaidObligationsForUserQueryVariables,
} from './__generated__/PaidObligationsTable.gql';

const PAGE_SIZE = 30;

const GET_PAID_OBLIGATIONS_FOR_USER = gql`
  query GetPaidObligationsForUser($paidPagination: PaginationInput) {
    getPaidObligationsForUser(paidPagination: $paidPagination) {
      edges {
        node {
          ...ObligationsTableRow
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${BaseObligationsTable.Fragment}
`;

const PaidObligationsTable = () => {
  const { data, loading, error, fetchMore } = useQuery<
    GetPaidObligationsForUserQuery,
    GetPaidObligationsForUserQueryVariables
  >(GET_PAID_OBLIGATIONS_FOR_USER, {
    variables: {
      paidPagination: {
        cursor: undefined,
        limit: PAGE_SIZE,
        direction: PaginationDirection.Forward,
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const rows = (data?.getPaidObligationsForUser.edges ?? []).map((edge) => edge.node);
  const isLoadingInitial = loading && rows.length === 0;
  const isLoadingMore = loading && rows.length > 0;
  const pageInfo = data?.getPaidObligationsForUser.pageInfo;

  const handleLoadMore = React.useCallback(async () => {
    if (!pageInfo?.endCursor) {
      return;
    }
    await fetchMore({
      variables: {
        paidPagination: {
          cursor: pageInfo.endCursor,
          limit: PAGE_SIZE,
          direction: PaginationDirection.Forward,
        },
      },
    });
  }, [fetchMore, pageInfo?.endCursor]);

  return (
    <BaseObligationsTable
      title="Paid"
      rows={rows}
      dateFormat={ObligationsDateFormat.Absolute}
      amountFormat={ObligationsAmountFormat.Full}
      emptyStateMessage="No paid bills or subscriptions"
      isLoading={isLoadingInitial}
      isLoadingMore={isLoadingMore}
      hasError={Boolean(error)}
      hasNextPage={data?.getPaidObligationsForUser.pageInfo?.hasNextPage ?? false}
      onLoadMore={handleLoadMore}
    />
  );
};

export default PaidObligationsTable;
